/**
 * Which row of the desktop week grid is open, and how tall it gets.
 *
 * Every row used to be as tall as its tallest card (192px), so a single
 * Saturday breakfast and a single Monday lunch took ~400px between them and
 * pushed dinner — the meal most cooks plan around — under the fold of a
 * 900px screen. Now one row is open at a time and the other two are thin
 * strips: the open row takes the height they gave up.
 *
 * Dinner is open by default. Clicking anything in a strip opens its row, and
 * the choice is kept on the device.
 */
import { rowOf } from '../constants';

export type FocusRow = 0 | 1 | 2;

/** Dinner: the row a cook with no stored choice plans around. */
export const DEFAULT_FOCUS_ROW: FocusRow = 2;

/** A strip: a 40px photo, two lines of title, 5px of padding around them. */
export const COMPACT_ROW_HEIGHT = 50;

/** Never shorter than the card it replaces, never a wall of photo. */
export const MIN_OPEN_ROW_HEIGHT = 220;
export const MAX_OPEN_ROW_HEIGHT = 440;

/** A stored value from an older or hand-edited profile falls back to dinner. */
export function normalizeFocusRow(value: unknown): FocusRow {
  return value === 0 || value === 1 || value === 2 ? value : DEFAULT_FOCUS_ROW;
}

/**
 * The row drawn open. While the Library drawer is filling a slot, that slot's
 * row is open whatever was chosen, so the hovered recipe previews in a real
 * cell — but the stored choice is not rewritten by it.
 */
export function effectiveFocusRow(stored: unknown, placeTargetSk: number | null | undefined): FocusRow {
  if (placeTargetSk != null && placeTargetSk >= 0) return normalizeFocusRow(rowOf(placeTargetSk));
  return normalizeFocusRow(stored);
}

/**
 * Height of the open row: whatever the scroll area has left once the day
 * header, the two strips and the gaps are paid for, clamped.
 *
 * `chrome` is everything in the scroll area that is not a row — its padding,
 * the day header, the gaps between rows.
 */
export function openRowHeight(viewportHeight: number, chrome: number): number {
  if (!(viewportHeight > 0)) return MIN_OPEN_ROW_HEIGHT;
  const left = viewportHeight - chrome - 2 * COMPACT_ROW_HEIGHT;
  return Math.round(Math.min(MAX_OPEN_ROW_HEIGHT, Math.max(MIN_OPEN_ROW_HEIGHT, left)));
}
